// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getErrorMessages } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import {
    CartLineModel,
    ProductSettingsModel,
    ProductSubscriptionDto,
    PromotionModel,
} from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import XCircle from "@insite/mobius/Icons/XCircle";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineContainer, { CartLineContainerStyles } from "@steiner/App/Components/Cart/CartLineContainer";
import Notify from "@steiner/App/Components/Notify";
import setCutProductsModalOpen from "@steiner/App/Handlers/ProductDetails/SetCutProductsModalOpen";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { isBidItem } from "@steiner/App/Store/Selectors/CartsSelector";
import Tooltip, { TooltipPresentationProps } from "@steiner/MobiusOverrides/Tooltip";
import ProductPartNumbers from "@steiner/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@steiner/Overrides/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@steiner/Overrides/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@steiner/Overrides/Widgets/Cart/CartLineQuantity";
import { parseBoolean } from "@steiner/XDL/Utilities";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // xng-change
    editable?: boolean;
    editableNote?: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    // XNG-Changes
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    isCartPage: getCurrentPage(state).type === "CartPage",
    isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
    isRfqConfirmationPage: getCurrentPage(state).type === "RfqConfirmationPage",
    isOrderApprovalDetailsPage: getCurrentPage(state).type === "OrderApprovalDetailsPage",
    canApproveOrders: getCurrentAccountState(state)?.value?.canApproveOrders,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    // XNG-Changes
    setCutProductsModalOpen,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    cartLineContainer?: CartLineContainerStyles;
    extendedUnitNetPriceWrapper?: InjectableCss;
    extendedUnitNetPriceHeadingAndText?: SmallHeadingAndTextStyles;
    tooltip?: TooltipPresentationProps;
    tooltipContentContainer?: InjectableCss;
    tooltipIcon?: IconPresentationProps;
    tooltipContentText?: TypographyPresentationProps;
    priceNotAvailable?: TypographyPresentationProps;
    promotionIcon?: IconPresentationProps;
    notesWrapper?: InjectableCss;
    addCutLengthsLink?: LinkPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: { width: 6 },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = false,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    // XNG-Changes
    editable,
    editableNote = true,
    history,
    cartPageUrl,
    isCartPage,
    isOrderConfirmationPage,
    isRfqConfirmationPage,
    isOrderApprovalDetailsPage,
    canApproveOrders,
    setCutProductsModalOpen,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        // XNG-Changes
        removeCartLine({
            cartLineId: cartLine.id,
            onComplete: () => {
                if (cart.cartLines?.length === 1 && cartPageUrl) {
                    history.push(cartPageUrl);
                }
            },
        });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const errorMessages = getErrorMessages(cart, cartLine, showInventoryAvailability);

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    // xng-change
    const isWidgetNonStockItem = parseBoolean(cartLine.properties?.isWidgetNonStockItem);

    // <xng-changes>
    const showAddToProjectsLink =
        showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && !isWidgetNonStockItem;

    const cutMessage = cartLine?.properties?.cut
        ? siteMessage("Custom_Edit_Cut_Length_Message")
        : siteMessage("Custom_Add_Cut_Length_Message");

    const handleOpenCutProductsModal = (product: CartLineModel) => {
        setCutProductsModalOpen({ isOpen: true, product });
    };
    // </xng-changes>

    // xng-change: change structure
    return (
        <CartLineContainer extendedStyles={styles.cartLineContainer}>
            <CartLineContainer.Image>
                {isWidgetNonStockItem ? (
                    <LazyImage
                        src={cartLine.smallImagePath}
                        altText={`${translate("Image of ")}: ${cartLine.properties?.nonStockDescription}`}
                        {...styles.productImage}
                    />
                ) : (
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                )}
                {parseBoolean(cartLine.properties.isRequired) && (
                    <Tooltip
                        triggerComponent={
                            <StyledWrapper {...styles.tooltipContentContainer}>
                                <Icon src={AlertTriangle} {...styles.tooltipIcon} />
                                <Typography {...styles.tooltipContentText}>{translate("Required")}</Typography>
                            </StyledWrapper>
                        }
                        text={siteMessage("STR_CartLines_Required_Product_Msg") as string}
                        {...styles.tooltip}
                    />
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <StyledWrapper>
                    {isWidgetNonStockItem ? (
                        <Typography {...styles.productDescription?.productDetailDescription} className="no-ellipses">
                            {cartLine.properties?.nonStockDescription}
                        </Typography>
                    ) : (
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    )}
                    {showBrand && cartLine.brand && (
                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                    )}
                </StyledWrapper>
                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                    <StyledWrapper {...styles.configurationGridItem}>
                        {cartLine.sectionOptions!.map(option => (
                            <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                {`${option.sectionName}:${option.optionName}`}
                            </Typography>
                        ))}
                    </StyledWrapper>
                )}
                <ProductPartNumbers
                    productNumber={isWidgetNonStockItem ? cartLine.properties?.nonStockPartNo : cartLine.erpNumber}
                    customerProductNumber={cartLine.customerName}
                    showManufacturerItem={!isWidgetNonStockItem}
                    manufacturerItem={cartLine.manufacturerItem}
                    showLabel={true}
                    extendedStyles={styles.productPartNumbers}
                    erpNumberLabel={isWidgetNonStockItem ? translate("Part #") : ""}
                />
                {showVmiBinDetails && currentMode === "Vmi" && (
                    <ProductBinNumbers
                        locationId={cart.vmiLocationId}
                        binId={cartLine.vmiBinId}
                        isCondensed={false}
                        extendedStyles={styles.productBinNumbers}
                    />
                )}
                {cartLine.isPromotionItem && editable && (
                    <Notify type="info">{siteMessage("Cart_PromotionalItem")}</Notify>
                )}
                {cartLine.isSubscription && cartLine.productSubscription && (
                    <ProductDeliveryScheduleButton
                        subscription={
                            cartLine.properties.productSubscription
                                ? JSON.parse(cartLine.properties.productSubscription)
                                : cartLine.productSubscription
                        }
                        disabled={
                            !cart.canModifyOrder ||
                            cartLine.isPromotionItem ||
                            !cartLine.qtyOrdered ||
                            cartLine.qtyOrdered <= 0
                        }
                        onSave={saveScheduleHandler}
                        extendedStyles={styles.deliveryScheduleButton}
                    />
                )}
                {cart.showCostCode && !cartLine.isPromotionItem && (
                    <StyledWrapper {...styles.costCodeGridItem}>
                        <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                        {cart.canEditCostCode && editable ? (
                            <Select
                                value={cartLine.costCode}
                                onChange={costCodeChangeHandler}
                                aria-label={translate("Cost Code")}
                                {...styles.costCodeSelect}
                            >
                                {cart.costCodes?.map(costCode => (
                                    <option key={costCode.costCode} value={costCode.costCode}>
                                        {costCode.description}
                                    </option>
                                ))}
                            </Select>
                        ) : (
                            <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                        )}
                    </StyledWrapper>
                )}
                {/* XNG-Changes */}
                {parseBoolean(cartLine.properties?.allowCuts) && isCartPage && !!cutMessage?.toString() && (
                    <Notify>{cutMessage}</Notify>
                )}
            </CartLineContainer.Description>
            <CartLineContainer.Inventory>
                {showInventoryAvailability && !cartLine.quoteRequired && !isWidgetNonStockItem && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                    />
                )}
            </CartLineContainer.Inventory>
            <CartLineContainer.Price>
                {!isWidgetNonStockItem || isBidItem(cartLine) ? (
                    <ProductPrice
                        product={cartLine}
                        currencySymbol={cart.currencySymbol}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        forceShowPrice={true}
                        extendedStyles={styles.productPrice}
                        showLabel={showPriceLabel}
                        showUnitOfMeasure={showPerUnitPrice}
                        showSalePriceLabel={false}
                    />
                ) : (
                    <Typography {...styles.priceNotAvailable}>{siteMessage("STR_Price_Not_Available")}</Typography>
                )}

                {showPricing &&
                    promotions?.map(promotion => (
                        <Typography {...styles.promotionNameText} key={promotion.id}>
                            <Icon {...styles.promotionIcon} /> {promotion.name}
                        </Typography>
                    ))}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                    editable={
                        editable &&
                        cartLine.properties.isBidItem !== "true" &&
                        !cartLine.isPromotionItem &&
                        // XNG-Changes
                        !cartLine.properties.cut
                    }
                    showLabel
                    label={translate("Qty")}
                    showAsNumbers={isOrderConfirmationPage || isRfqConfirmationPage || isOrderApprovalDetailsPage}
                />
                {errorMessages.length > 0 && (
                    <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                        {errorMessages.map((message, index) => (
                            <Typography
                                {...styles.cartLineErrorMessageText}
                                key={`${index}-${message}`}
                                data-test-selector="cartLine_errorMessage"
                            >
                                {message}
                            </Typography>
                        ))}
                    </StyledWrapper>
                )}
            </CartLineContainer.Qty>
            <CartLineContainer.Notes>
                {/* XNG-Changes */}
                <StyledWrapper {...styles.notesWrapper} {...{ showAddToProjectsLink }}>
                    {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <CartLineNotes
                            cart={cart}
                            editable={editableNote}
                            onNotesChange={notesChangeHandler}
                            extendedStyles={styles.cartLineNotes}
                            showAddToProjectsLink={showAddToProjectsLink}
                            addToProjectsClickHandler={addToListClickHandler}
                            placeholder={translate("Add your notes here")}
                        />
                    )}
                    {parseBoolean(cartLine.properties?.allowCuts) &&
                        (editable ||
                            (isOrderApprovalDetailsPage && !canApproveOrders && cartLine?.properties?.cut)) && (
                            <Link onClick={() => handleOpenCutProductsModal(cartLine)} {...styles.addCutLengthsLink}>
                                {cartLine?.properties?.cut ? translate("Edit Cut Length") : translate("Add Cut Length")}
                            </Link>
                        )}
                </StyledWrapper>
            </CartLineContainer.Notes>
            <CartLineContainer.Total>
                {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        <StyledWrapper {...styles.extendedUnitNetPriceWrapper}>
                            <SmallHeadingAndText
                                {...styles.extendedUnitNetPriceHeadingAndText}
                                data-test-selector="cartLine_extendedUnitNetPrice"
                                heading={translate("Ext. Price")}
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                            />
                        </StyledWrapper>
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.subtotalWithoutVatText}>
                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                )}
            </CartLineContainer.Total>
            <CartLineContainer.Remove>
                {showRemoveAction &&
                    !cartLine.isPromotionItem &&
                    cart.canModifyOrder &&
                    editable &&
                    cartLine.properties["isBidItem"] !== "true" && (
                        <Clickable
                            onClick={removeCartLineClickHandler}
                            data-test-selector="cartLine_removeLine"
                            aria-label={translate("Remove item")}
                        >
                            <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                            <IconMemo {...styles.removeCartLineIcon} />
                        </Clickable>
                    )}
            </CartLineContainer.Remove>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(withHistory(CartLineCardExpanded)));
